/* eslint-disable max-lines */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
	Spinner, 
	Flex, 
	Button, 
	Text,
	Link,
	Box
} from '@chakra-ui/react';
import { useSignMutation, useTryCompleteSurveyMutation } from 'services';
import { useLocale } from 'context/LocalizationContext';
import { useAppSelector, useToast } from 'Hooks';
import { UserPreferenceFormName } from 'Types/Enums/UserPreferenceEnums';
import { getValueName, useDefaultErrorHandler } from 'Utils';
import ErrorCodes from 'Data/ErrorCodes';
import { useSurveyExit } from 'Utils';

// Define types for Chatbase events
interface UserMessageEvent {
	type: string;
	data: {
		content: string;
	};
}

interface AssistantMessageEvent {
	type: string;
	data: {
		content: string;
	};
}

// Extend Window interface to include chatbase
declare global {
	interface Window {
		chatbase: any;
	}
}

interface ChatbaseSurveyProps {
	onComplete: () => void;
}

export const ChatbaseSurvey = ({ onComplete }: ChatbaseSurveyProps) => {
	const { userInfo: user } = useAppSelector((state) => state.authUser);
	const { activeSurvey, section } = useAppSelector((state) => state.survey);
	const { locale } = useLocale();
	const userId = user?.id;
	const surveyId = activeSurvey?.survey?.id;

	// State for "Continue to Brand Permissions" button
	const [showContinueButton, setShowContinueButton] = useState(false);
	const [isContinueButtonLoading, setIsContinueButtonLoading] = useState(false);
	
	// State for "Having Trouble?" bubble
	const [isHelpBubbleVisible, setIsHelpBubbleVisible] = useState(false);
	
	// State to track if widget is loaded
	const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
	
	// Track event listener timeouts for proper cleanup
	const eventListenerTimeoutsRef = useRef<NodeJS.Timeout[]>([]);

	const handleSurveyExit = useSurveyExit();

	if (!activeSurvey?.survey?.chatbaseAgentId) {
		throw new Error(ErrorCodes.GetSurveyAgentId);
	}

	const [signMutation, { data: signData, isLoading: isSignLoading }] = useSignMutation();
	const [tryCompleteSurvey, {
		isError: isTryCompleteError,
		error: tryCompleteError,
		isSuccess: isTryCompleteSuccess,
		data: tryCompleteData
	}] = useTryCompleteSurveyMutation();
	const { alert } = useToast();

	useDefaultErrorHandler(isTryCompleteError, tryCompleteError);

	const setChatbaseIdentity = () => {
		if (userId && signData?.data) {
			window.chatbase('identify', {
				user_id: userId,
				user_hash: signData.data,
				user_metadata: {
					name: user?.fullName || user?.firstName || 'User',
					email: user?.emailAddress || '',
					gender: getValueName(UserPreferenceFormName.Gender, user?.gender) || '',
					surveyId: surveyId || '',
					qrCodeId: activeSurvey?.surveyQRWebCodeId || '',
					// Add any other relevant user information
				}
			});
		}
	};

	const openChatbaseWidget = () => {
		if (window.chatbase) {
			window.chatbase('open');
			// Mark widget as loaded after it opens
			setTimeout(() => {
				setIsWidgetLoaded(true);
			}, 500);
		}
	};

	// Helper function to safely add event listeners with timeout tracking
	const addEventListenersWithTimeout = (delay: number) => {
		const timeoutId = setTimeout(() => {
			if (window.chatbase && window.chatbase.addEventListener) {
				window.chatbase.addEventListener('user-message', handleUserMessage);
				window.chatbase.addEventListener('assistant-message', handleAssistantMessage);
			} else {
				console.warn('[Chatbase] Could not add event listeners - chatbase or addEventListener not available');
			}
			openChatbaseWidget();
		}, delay);

		eventListenerTimeoutsRef.current.push(timeoutId);
		return timeoutId;
	};

	// Event handler for user-message events
	const handleUserMessage = useCallback((event: any) => {
		const userMessageEvent = event as UserMessageEvent;
		console.log('Event received: user-message', userMessageEvent.data.content);
	}, []);

	// Event handler for assistant-message events
	const handleAssistantMessage = useCallback((event: any) => {
		const assistantMessageEvent = event as AssistantMessageEvent;
		//console.log('Event received: assistant-message', assistantMessageEvent.data.content);

		// Check if message contains the completion phrase to show the continue button
		const completionPhrase = activeSurvey?.survey?.chatbaseCompletionPhrase;
		if (completionPhrase && assistantMessageEvent.data.content.toLowerCase().includes(completionPhrase.toLowerCase())) {
			setShowContinueButton(true);
			setIsContinueButtonLoading(false);
		}
	}, [activeSurvey?.survey?.chatbaseCompletionPhrase]);

	// Handle continue button click
	const handleContinueClick = () => {
		setIsContinueButtonLoading(true);

		// Try to complete the survey before proceeding
		const qrCodeId = activeSurvey?.surveyQRWebCodeId;
		if (qrCodeId) {
			tryCompleteSurvey({ qrCodeId });
		}
	};

	// Handle survey completion result
	useEffect(() => {
		if (isTryCompleteSuccess) {
			if (!tryCompleteData?.data) {
				// Survey not completed, don't proceed
				alert('error', 'Please complete all survey questions before continuing.');
				setIsContinueButtonLoading(false);
				return;
			}

			// Close the Chatbase widget immediately before navigation to prevent visual glitch
			if (window.chatbase && typeof window.chatbase === 'function') {
				try {
					window.chatbase('close');
				} catch (error) {
					console.warn('Error closing Chatbase widget on continue:', error);
				}
			}

			// Call the onComplete callback to move to brand permissions
			onComplete();
		}
	}, [isTryCompleteSuccess, tryCompleteData]);

	// Reset loading state on error
	useEffect(() => {
		if (isTryCompleteError) {
			setIsContinueButtonLoading(false);
		}
	}, [isTryCompleteError]);

	// Handle toggling help bubble
	const handleToggleHelpBubble = () => setIsHelpBubbleVisible(!isHelpBubbleVisible);

	// Get user hash for identity verification
	useEffect(() => {
		if (surveyId) {
			signMutation({ surveyId, locale });
		}
	}, [surveyId, locale]);

	useEffect(() => {
		// Only initialize Chatbase when we have the agent ID and user hash
		const agentId = activeSurvey?.survey?.chatbaseAgentId;
		if (!agentId || !signData?.data || !userId) return;
		
		// Additional safety check - ensure we're still in the questions section
		if (section !== 'questions') return;


		// Generate widget styles for different screen configurations
		const generateWidgetStyles = (config: {
			width: string;
			height: string;
			left: string;
			right: string;
			top: string;
			transform: string;
		}) => {
			// Target specific Chatbase widget container ID and common class names
			// Avoid broad selectors like [id*="chatbase"] that catch everything
			const selectors = [
				'#chatbase-bubble-window',
				'.chatbase-widget',
				'.chatbase-widget-container',
				'[data-chatbase-widget]'
			].join(', ');

			return `${selectors} {
				width: ${config.width} !important;
				max-width: ${config.width} !important;
				min-width: ${config.width} !important;
				height: ${config.height} !important;
				max-height: ${config.height} !important;
				min-height: ${config.height} !important;
				left: ${config.left} !important;
				right: ${config.right} !important;
				top: ${config.top} !important;
				bottom: auto !important;
				transform: ${config.transform} !important;
				position: fixed !important;
				z-index: 9999 !important;
				border-radius: 12px !important;
				box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
			}`;
		};

		// Add custom CSS to make Chatbase widget take full width and height
		const addCustomStyles = () => {
			const styleId = 'chatbase-custom-styles';
			if (!document.getElementById(styleId)) {
				const style = document.createElement('style');
				style.id = styleId;
				style.textContent = `
/* Desktop styles - centered 600px wide, positioned under header */
${generateWidgetStyles({
		width: '600px',
		height: 'calc(100dvh - 238px)',
		left: '50%',
		right: 'auto',
		top: '138px',
		transform: 'translateX(-50%)'
	})}

/* Target the main chat container and iframes - use specific selectors */
#chatbase-bubble-window iframe,
#chatbase-bubble-window > div,
.chatbase-widget iframe,
.chatbase-widget > div,
[data-chatbase-widget] iframe,
[data-chatbase-widget] > div {
	width: 100% !important;
	height: 100% !important;
	max-width: 100% !important;
	max-height: 100% !important;
	min-width: 100% !important;
	min-height: 100% !important;
	border-radius: 12px !important;
}

/* Small screens (480px to 767px) - header 118px + padding 70px = 188px total */
@media (min-width: 480px) and (max-width: 767px) {
	${generateWidgetStyles({
		width: 'calc(100vw - 40px)',
		height: 'calc(100dvh - 238px)',
		left: '20px',
		right: '20px',
		top: '138px',
		transform: 'none'
	})}
}

/* Mobile screens (up to 479px) - header 60px + padding 30px = 90px total */
@media (max-width: 479px) {
	${generateWidgetStyles({
		width: 'calc(100vw - 40px)',
		height: 'calc(100dvh - 170px)',
		left: '20px',
		right: '20px',
		top: '80px',
		transform: 'none'
	})}
}

/* Hide the Chatbase bubble button */
#chatbase-bubble-button {
	display: none !important;
	visibility: hidden !important;
	opacity: 0 !important;
}
				`;
				document.head.appendChild(style);
			}
		};

		let windowLoadCleanup: (() => void) | null = null;

		// Check if script already exists to prevent duplicate loading
		const existingScript = document.getElementById(agentId);
		if (existingScript) {
			// Script already exists, just set identity if needed
			setChatbaseIdentity();
			addCustomStyles();
			
			// Add event listeners if Chatbase is available
			addEventListenersWithTimeout(100);
			return;
		}

		// Chatbase bot script with identity verification
		const onLoad = function() {
			const script = document.createElement('script');
			script.src = 'https://www.chatbase.co/embed.min.js';
			script.id = agentId;
			(script as any).domain = 'www.chatbase.co';
			document.body.appendChild(script);
			
			// Add custom styles after script loads
			addCustomStyles();
			
			setChatbaseIdentity();
			
			// Add event listeners after script loads and Chatbase is initialized
			addEventListenersWithTimeout(1000);
		};

		// Initialize Chatbase
		(function() {
			if (!window.chatbase || window.chatbase('getState') !== 'initialized') {
				window.chatbase = (...args: any[]) => {
					if (!window.chatbase.q) {
						window.chatbase.q = [];
					}
					window.chatbase.q.push(args);
				};
				window.chatbase = new Proxy(window.chatbase, {
					get(target: any, prop: string) {
						if (prop === 'q') {
							return target.q;
						}
						return (...args: any[]) => target(prop, ...args);
					}
				});
			}
			
			if (document.readyState === 'complete') {
				onLoad();
			} else {
				window.addEventListener('load', onLoad);
				windowLoadCleanup = () => window.removeEventListener('load', onLoad);
			}
		})();

		// Cleanup function
		return () => {
			if (windowLoadCleanup) {
				windowLoadCleanup();
			}
		};
	}, [activeSurvey?.survey?.chatbaseAgentId, userId, signData?.data, user]);

	// Comprehensive cleanup on component unmount
	useEffect(() => {
		return () => {
			// 1. Clear any pending timeouts
			eventListenerTimeoutsRef.current.forEach(timeoutId => {
				clearTimeout(timeoutId);
			});
			eventListenerTimeoutsRef.current = [];
			
			// 2. Remove event listeners if they were added
			if (window.chatbase && window.chatbase.removeEventListener) {
				window.chatbase.removeEventListener('user-message', handleUserMessage);
				window.chatbase.removeEventListener('assistant-message', handleAssistantMessage);
			}
			
			// 3. Close/hide the Chatbase widget (documented API)
			if (window.chatbase && typeof window.chatbase === 'function') {
				try {
					window.chatbase('close');
				} catch (error) {
					console.warn('Error closing Chatbase widget:', error);
				}
			}
			
			// 4. Remove all Chatbase scripts from the DOM
			document.querySelectorAll('script[src*="chatbase.co"]').forEach(script => {
				script.remove();
			});
			
			// 5. Remove any Chatbase DOM containers
			document.querySelectorAll('[class*="chatbase"]').forEach(element => {
				element.remove();
			});
			document.querySelectorAll('[id*="chatbase"]').forEach(element => {
				element.remove();
			});
			
			// 6. Remove custom styles
			const customStyle = document.getElementById('chatbase-custom-styles');
			if (customStyle) {
				customStyle.remove();
			}
			
			// 7. Delete the Chatbase object from window
			delete window.chatbase;
			
			// 8. Reset widget loaded state
			setIsWidgetLoaded(false);
		};
	}, [handleUserMessage, handleAssistantMessage]);

	if (isSignLoading) {
		return (
			<Flex
				justifyContent={'center'}
				alignItems={'center'}
				padding="6"
				boxShadow='lg'
				bg='white'
				h={'100dvh'}
				w={'full'}
			>
				<Spinner size='xl' color='darkCoral' />
			</Flex>
		);
	}

	return (
		<>
			{isWidgetLoaded && !showContinueButton && (
				<Flex
					position="fixed"
					bottom={'60px'}
					left={['30px', null, 'calc(50% - 280px)']}
					right={['30px', null, 'calc(50% - 280px)']}
					zIndex={10001}
					justifyContent="space-between"
					alignItems="flex-end"
				>
					{/* Exit Survey Link - Left */}
					<Link
						color="loopGrey.300"
						fontSize={['12px', '14px']}
						fontWeight="400"
						textDecoration="underline"
						_hover={{
							color: 'gray.700',
							textDecoration: 'underline'
						}}
						cursor="pointer"
						onClick={() => handleSurveyExit()}
					>
						Exit Survey
					</Link>

					{/* Having Trouble Link with Bubble - Right */}
					<Flex
						direction="column"
						alignItems="flex-end"
					>
						{/* Help Text Bubble */}
						{isHelpBubbleVisible && (
							<Box
								mb={2}
								bg="white"
								border="1px solid"
								borderColor="gray.200"
								borderRadius="8px"
								boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
								p={4}
								maxW={['250px', '300px']}
								position="relative"
								_after={{
									content: '""',
									position: 'absolute',
									bottom: '-8px',
									right: '20px',
									width: 0,
									height: 0,
									borderLeft: '8px solid transparent',
									borderRight: '8px solid transparent',
									borderTop: '8px solid white',
								}}
								_before={{
									content: '""',
									position: 'absolute',
									bottom: '-9px',
									right: '19px',
									width: 0,
									height: 0,
									borderLeft: '9px solid transparent',
									borderRight: '9px solid transparent',
									borderTop: '9px solid',
									borderTopColor: 'gray.200',
								}}
							>
								<Text fontSize={['12px', '14px']} lineHeight="1.5" color="gray.700" mb={2}>
									If you&apos;ve finished the survey but don&apos;t see a &quot;Next&quot; button, ask the bot: &quot;Have I completed the survey?&quot;
								</Text>
								<Text fontSize={['12px', '14px']} lineHeight="1.5" color="gray.700">
									Still no luck? Contact us at{' '}
									<Link 
										href="mailto:admin@feedtheloop.com" 
										color="darkCoral"
										fontWeight="500"
										textDecoration="underline"
										_hover={{
											color: 'coral'
										}}
									>
										admin@feedtheloop.com
									</Link>
								</Text>
							</Box>
						)}
						
						{/* Having Trouble Link */}
						<Link
							color="darkCoral"
							fontSize={['12px', '14px']}
							fontWeight="500"
							textDecoration="underline"
							_hover={{
								color: 'coral',
								textDecoration: 'underline'
							}}
							cursor="pointer"
							onClick={handleToggleHelpBubble}
						>
							Having Trouble?
						</Link>
					</Flex>
				</Flex>
			)}

			{/* Continue to Brand Permissions Button */}
			{showContinueButton && (
				<Flex
					position="fixed"
					bottom="20px"
					left="50%"
					transform="translateX(-50%)"
					zIndex={10000}
					justifyContent="center"
					alignItems="center"
				>
					<Button
						variant='primary'
						isLoading={isContinueButtonLoading}
						size="lg"
						width={['calc(100vw - 40px)', '600px']}
						px={8}
						py={6}
						borderRadius="12px"
						boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
						onClick={handleContinueClick}
					>
						Continue to Brand Permissions
					</Button>
				</Flex>
			)}
		</>
	);
};
